// LLM generated - Model Claude 4 Sonnet
import React, { useEffect, useRef, useState, useCallback } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { Post } from '../Post/Post';
import { PostSkeleton } from '../PostSkeleton/PostSkeleton';
import { useStyles } from './PostsList.styles';
import type { PostsListProps } from './types';

function PostsList({
  posts,
  currentUserHandle,
  onReportClick,
  onReactionSelect,
  onReactionRemove,
  availableReactions = [],
  isLoadingReactions = false,
  getUserReactions,
  loaderRef,
  scrollContainerRef,
  hasNextPage,
  isFetchingNextPage,
  isLoading = false,
}: PostsListProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('chat');
  const hasInitializedScroll = useRef(false);
  const [expandedPostIds, setExpandedPostIds] = useState<Set<string>>(new Set());

  const handleToggleExpand = useCallback((postId: string) => {
    setExpandedPostIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  }, []);

  // Initialize scroll position to top when posts first load (for posts, newest should be visible first)
  useEffect(() => {
    if (scrollContainerRef?.current && posts.length > 0 && !hasInitializedScroll.current) {
      scrollContainerRef.current.scrollTop = 0;
      hasInitializedScroll.current = true;
    }
  }, [posts.length, scrollContainerRef]);

  // For posts: newest are already at the top from parent component sorting
  // No need to reverse - posts should display newest at top (like social media feed)

  // Show skeleton loading state during initial load
  if (isLoading && posts.length === 0) {
    return (
      <div className={classes.container}>
        <div ref={scrollContainerRef} className={classes.scrollContainer}>
          {/* Show 2-3 skeleton posts */}
          <div className={classes.postItem}>
            <PostSkeleton />
          </div>
          <div className={classes.postItem}>
            <PostSkeleton />
          </div>
          <div className={classes.postItem}>
            <PostSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div ref={scrollContainerRef} className={classes.scrollContainer}>
        {posts.map((post, index) => {
          const postId = post?.id;
          const isExpanded = expandedPostIds.has(postId);

          return (
            <div key={post?.id} className={classes.postItem}>
              <Post
                {...post}
                isExpanded={isExpanded}
                onToggleExpand={handleToggleExpand}
                onReportClick={onReportClick}
                onReactionSelect={onReactionSelect}
                onReactionRemove={onReactionRemove}
                availableReactions={availableReactions}
                isLoadingReactions={isLoadingReactions}
                currentUserHandle={currentUserHandle}
                userReactions={getUserReactions ? getUserReactions(post?.id) : []}
              />
            </div>
          );
        })}

        {/* Loader at bottom for "scroll down to load more older posts" */}
        {hasNextPage && (
          <div ref={loaderRef} className={classes.loader}>
            {isFetchingNextPage ? t('loading.olderPosts') : ''}
          </div>
        )}
      </div>
    </div>
  );
}

PostsList.displayName = 'PostsList';

export { PostsList };
